import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  getJob,
  pauseJob,
  resumeJob,
  cancelJob,
  type ReindexJobDetail as JobDetail,
  type ReindexStatus,
} from '@/services/reindex-api';
import { ArrowLeft, Pause, Play, X, ExternalLink, AlertTriangle, Loader2, Download } from 'lucide-react';
import { useState } from 'react';

const TERMINAL_STATUSES = new Set<ReindexStatus>([
  'completed',
  'failed',
  'cancelled',
]);

function statusVariant(status: ReindexStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'completed':
      return 'default';
    case 'failed':
    case 'cancelled':
      return 'destructive';
    case 'paused':
    case 'awaiting_confirmation':
      return 'secondary';
    default:
      return 'outline';
  }
}

function formatDuration(start: string | null, end: string | null): string {
  if (!start) return '—';
  const ms = (end ? new Date(end).getTime() : Date.now()) - new Date(start).getTime();
  const seconds = Math.floor(ms / 1000);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
}

function formatAgo(timestamp: string | null): string {
  if (!timestamp) return 'never';
  const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}

function ProgressBar({ value, max, color }: { value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="h-3 w-full overflow-hidden rounded-full bg-secondary">
      <div className={color} style={{ width: `${pct}%`, height: '100%' }} />
    </div>
  );
}

function CsvDownloadLink({ jobId, status, label }: { jobId: string; status: string; label: string }) {
  return (
    <a
      href={`/api/jobs/${jobId}/export?status=${status}`}
      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
      // Browser downloads via Content-Disposition; no extra attr needed.
    >
      <Download className="h-3 w-3" />
      {label}
    </a>
  );
}

export function ReindexJobDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deleteStaleIds, setDeleteStaleIds] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['reindex-job', id],
    queryFn: () => getJob(id!),
    enabled: Boolean(id),
    refetchInterval: (q) => {
      const job = q.state.data?.job;
      return job && TERMINAL_STATUSES.has(job.status) ? false : 5000;
    },
  });

  const job: JobDetail | undefined = data?.job;

  const handleAction = async (action: 'pause' | 'resume' | 'cancel') => {
    if (!id) return;
    setActionError(null);
    try {
      if (action === 'pause') await pauseJob(id);
      if (action === 'resume') await resumeJob(id);
      if (action === 'cancel') await cancelJob(id);
      qc.invalidateQueries({ queryKey: ['reindex-job', id] });
    } catch (err) {
      setActionError((err as Error).message);
    }
  };

  const handleConfirmStart = async () => {
    if (!id) return;
    setActionError(null);
    try {
      await resumeJob(id, { deleteStaleIds });
      setShowConfirmModal(false);
      qc.invalidateQueries({ queryKey: ['reindex-job', id] });
    } catch (err) {
      setActionError((err as Error).message);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-1/2" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (error || !job) {
    return (
      <div className="text-sm text-destructive">
        Failed to load job: {error ? (error as Error).message : 'not found'}
      </div>
    );
  }

  const totalForEnqueueBar = job.totalEstimate ?? Math.max(1, job.enqueuedCount);
  const totalForAckBar = Math.max(1, job.enqueuedCount);

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate('/reindex')} className="gap-2">
        <ArrowLeft className="h-4 w-4" /> Back to jobs
      </Button>

      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-3xl font-bold tracking-tight">{job.entityType} reindex</h2>
            <Badge variant={statusVariant(job.status)}>{job.status}</Badge>
            <Badge variant="outline">{job.mode}</Badge>
          </div>
          <p className="text-muted-foreground mt-1">
            Target: <span className="font-mono">{job.targetIndex}</span> — started{' '}
            {job.startedAt ? new Date(job.startedAt).toLocaleString() : '—'} by {job.createdBy} —{' '}
            {formatDuration(job.startedAt, job.finishedAt)} elapsed
          </p>
        </div>
        <div className="flex gap-2">
          {!TERMINAL_STATUSES.has(job.status) && job.status !== 'paused' && (
            <Button variant="outline" onClick={() => handleAction('pause')}>
              <Pause className="mr-2 h-4 w-4" /> Pause
            </Button>
          )}
          {job.status === 'awaiting_confirmation' && (
            <Button
              onClick={() => {
                // If the operator opted into stale deletion, gate behind a
                // final "are you sure?" modal. Otherwise just start.
                if (deleteStaleIds) setShowConfirmModal(true);
                else handleConfirmStart();
              }}
            >
              <Play className="mr-2 h-4 w-4" /> Confirm & start
              {deleteStaleIds && ' (with deletion)'}
            </Button>
          )}
          {job.status === 'paused' && (
            <Button onClick={() => handleAction('resume')}>
              <Play className="mr-2 h-4 w-4" /> Resume
            </Button>
          )}
          {!TERMINAL_STATUSES.has(job.status) && (
            <Button variant="destructive" onClick={() => handleAction('cancel')}>
              <X className="mr-2 h-4 w-4" /> Cancel
            </Button>
          )}
        </div>
      </div>

      {actionError && <p className="text-sm text-destructive">{actionError}</p>}

      {/* Preflight running — long-running scan with no committed total yet.
          Show enough liveness signal (heartbeat freshness, cursor, last event)
          that the operator can tell it's working, not stuck. */}
      {(job.status === 'preflight' || job.status === 'queued') && (() => {
        const lastEvent = job.events[0];
        const mainCursor = job.cursors.find((c) => c.scope === 'main')?.cursor;
        const heartbeatAge = job.lastHeartbeat
          ? Math.floor((Date.now() - new Date(job.lastHeartbeat).getTime()) / 1000)
          : null;
        // Stale if heartbeat is older than 90s (workers heartbeat every ~30s).
        const heartbeatStale = heartbeatAge != null && heartbeatAge > 90;
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                Preflight running — discovering missing & stale documents
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-4 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Elapsed
                  </div>
                  <div className="tabular-nums">
                    {formatDuration(job.startedAt, null)}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Source IDs scanned
                  </div>
                  <div className="tabular-nums">
                    {mainCursor ? Number(mainCursor).toLocaleString() : 'starting…'}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Missing found
                  </div>
                  <div className="tabular-nums">
                    {job.preflightMissingFound != null
                      ? job.preflightMissingFound.toLocaleString()
                      : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Worker heartbeat
                  </div>
                  <div className={heartbeatStale ? 'text-amber-600' : 'text-green-700'}>
                    {formatAgo(job.lastHeartbeat)}
                    {heartbeatStale && ' (stale)'}
                  </div>
                </div>
              </div>
              {lastEvent && (
                <div className="border-t pt-2 text-xs">
                  <span className="text-muted-foreground">Latest event:</span>{' '}
                  <span className="font-mono">{lastEvent.message}</span>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Large entity types can take 10+ minutes. The preflight streams every source ID
                from <span className="font-mono">Snowflake / Affinity</span> and compares against
                the target index.
              </p>
            </CardContent>
          </Card>
        );
      })()}

      {/* Awaiting confirmation — preflight summary, no progress bars yet. */}
      {job.status === 'awaiting_confirmation' && (
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader className="pb-2 flex flex-row items-start justify-between space-y-0">
              <CardTitle className="text-sm text-muted-foreground font-normal">
                Missing — will be added
              </CardTitle>
              {(job.totalEstimate ?? 0) > 0 && (
                <CsvDownloadLink jobId={job.id} status="pending" label="CSV" />
              )}
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold tabular-nums">
                {(job.totalEstimate ?? 0).toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                In source but not in <span className="font-mono">{job.targetIndex}</span>
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2 flex flex-row items-start justify-between space-y-0">
              <CardTitle className="text-sm text-muted-foreground font-normal">
                Stale — in OS, not in source
              </CardTitle>
              {(job.staleCount ?? 0) > 0 && (
                <CsvDownloadLink jobId={job.id} status="stale" label="CSV" />
              )}
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold tabular-nums text-amber-600">
                {(job.staleCount ?? 0).toLocaleString()}
              </div>
              {(job.staleCount ?? 0) > 0 ? (
                <label className="mt-3 flex items-start gap-2 cursor-pointer rounded-md border border-amber-300 bg-amber-50 p-2.5">
                  <Checkbox
                    checked={deleteStaleIds}
                    onCheckedChange={(v) => setDeleteStaleIds(v === true)}
                    className="mt-0.5"
                  />
                  <span className="text-sm">
                    <span className="font-medium text-amber-900">
                      Delete these {(job.staleCount ?? 0).toLocaleString()} stale docs from OpenSearch
                    </span>
                    <span className="block text-xs text-amber-700 mt-0.5">
                      Cannot be undone. Runs after the missing-doc ingest completes.
                    </span>
                  </span>
                </label>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">
                  No stale documents to clean up.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Running / awaiting_index / completed — progress bars are meaningful now. */}
      {(job.status === 'running' ||
        job.status === 'awaiting_index' ||
        job.status === 'completed') && (
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between space-y-0">
              <CardTitle className="text-sm">Enqueued to webhook</CardTitle>
              {job.enqueuedCount > 0 && (
                <CsvDownloadLink jobId={job.id} status="enqueued" label="CSV" />
              )}
            </CardHeader>
            <CardContent className="space-y-2">
              <ProgressBar
                value={job.enqueuedCount}
                max={totalForEnqueueBar}
                color="bg-blue-500"
              />
              <p className="text-sm text-muted-foreground">
                {job.enqueuedCount.toLocaleString()}
                {job.totalEstimate != null && (
                  <> / {job.totalEstimate.toLocaleString()} expected</>
                )}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-start justify-between space-y-0">
              <CardTitle className="text-sm">Indexed (acked)</CardTitle>
              {job.ackedCount > 0 && (
                <CsvDownloadLink jobId={job.id} status="indexed" label="CSV" />
              )}
            </CardHeader>
            <CardContent className="space-y-2">
              <ProgressBar
                value={job.ackedCount}
                max={totalForAckBar}
                color="bg-green-500"
              />
              <p className="text-sm text-muted-foreground">
                {job.ackedCount.toLocaleString()} / {job.enqueuedCount.toLocaleString()} enqueued
              </p>
            </CardContent>
          </Card>
          {job.errorCount > 0 && (
            <Card className="md:col-span-2 border-destructive/50">
              <CardHeader className="flex flex-row items-start justify-between space-y-0">
                <CardTitle className="text-sm text-destructive">Errors</CardTitle>
                <CsvDownloadLink jobId={job.id} status="failed" label="CSV" />
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  {job.errorCount.toLocaleString()} failed during indexing
                </p>
              </CardContent>
            </Card>
          )}
          {job.deleteStaleIds && job.staleCount != null && job.staleCount > 0 && (
            <Card className="md:col-span-2">
              <CardHeader className="flex flex-row items-start justify-between space-y-0">
                <CardTitle className="text-sm">Stale documents deleted</CardTitle>
                {job.staleDeletedCount > 0 && (
                  <CsvDownloadLink jobId={job.id} status="deleted" label="CSV" />
                )}
              </CardHeader>
              <CardContent className="space-y-2">
                <ProgressBar
                  value={job.staleDeletedCount}
                  max={Math.max(1, job.staleCount)}
                  color="bg-amber-500"
                />
                <p className="text-sm text-muted-foreground">
                  {job.staleDeletedCount.toLocaleString()} / {job.staleCount.toLocaleString()} stale
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Final safety modal — only shown when the operator opted into stale
          deletion. Missing-only confirms inline (no extra click). */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Confirm stale-document deletion
            </DialogTitle>
            <DialogDescription>
              You're about to permanently delete{' '}
              <strong>{(job.staleCount ?? 0).toLocaleString()}</strong> documents from{' '}
              <span className="font-mono">{job.targetIndex}</span> after the missing-doc
              ingest of <strong>{(job.totalEstimate ?? 0).toLocaleString()}</strong>{' '}
              {job.entityType} records completes. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmModal(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmStart}>
              <Play className="mr-2 h-4 w-4" />
              Yes, start with deletion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {job.status === 'completed' && (
        <Card className="border-green-500">
          <CardContent className="py-6">
            <h3 className="text-lg font-semibold">Completed</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {job.ackedCount.toLocaleString()} indexed, {job.errorCount.toLocaleString()} errors
              in {formatDuration(job.startedAt, job.finishedAt)}.
            </p>
            <a
              className="text-sm underline mt-2 inline-flex items-center gap-1"
              href={`https://search-entity-snapshots-index-rziuty4klro6pdk5nx6c4thsby.aos.us-east-1.on.aws/${encodeURIComponent(
                job.targetIndex,
              )}/_search?pretty&size=5`}
              target="_blank"
              rel="noreferrer"
            >
              View in OpenSearch <ExternalLink className="h-3 w-3" />
            </a>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Event log</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1 font-mono text-xs max-h-96 overflow-y-auto">
            {job.events.length === 0 ? (
              <p className="text-muted-foreground">No events yet.</p>
            ) : (
              job.events.map((ev) => (
                <div
                  key={ev.id}
                  className={
                    ev.level === 'error'
                      ? 'text-destructive'
                      : ev.level === 'warn'
                        ? 'text-yellow-600'
                        : 'text-muted-foreground'
                  }
                >
                  [{new Date(ev.createdAt).toLocaleTimeString()}] {ev.level.toUpperCase()}{' '}
                  {ev.message}
                  {ev.meta ? ' ' + JSON.stringify(ev.meta) : ''}
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
